/**
 * Sidebar content panel. The tab strip lives in the top HUD (see App)
 * and drives `activeTab`. This component only renders panel content;
 * the parent decides when to mount/unmount it.
 */

import { useState, type ReactNode } from "react";
import {
  Badge,
  Button,
  IconButton,
  Tooltip,
  useContextMenu,
  type ContextMenuItem,
} from "@archlang/ui";
import { ChevronLeft, Info, Pencil, Plus, TriangleAlert, X } from "lucide-react";
import { ProcessGlyph } from "../workbench/primitives/icons.js";
import { InlineText, InlineTextarea } from "./InlineEditable.js";
// Type-only (erased at build): the editing bundle the workbench injects. The
// standalone DetailsPanel never passes it, so this stays a view contract.
import type { ModuleEditing } from "../workbench/useModuleEditing.js";
import type { MutationResult } from "../workbench/mutations.js";
import type {
  Diagnostic,
  Interface,
  Module,
  Process,
  StableId,
} from "@archlang/scene/arch-model";
import type { BoardEdge, BoardSpace } from "../board";
import type { CallDetail, SpaceLike } from "@archlang/scene/board";
import { asSource, walkFocal } from "@archlang/scene/board";
import { flattenInterfaces, getTeam, moduleKey } from "@archlang/scene/arch-model";
import { processKey } from "../processKey.js";
import {
  descriptionToPlainText,
  handleDescriptionClick,
  renderDescriptionHtml,
} from "../description/render.js";
import { ProcessTree } from "../process/index.js";
import { Backlinks } from "./Backlinks";
import { lookupBacklinks } from "./lookupBacklinks.js";
import { interfaceDisplayName, isAnonymousInterface } from "../displayNames";
import { useTranslation, useLocale } from "../i18n";
import { diagnosticText } from "@archlang/engine";
import { aspectColorFor } from "../aspectColors.js";
import type { WireBacklink } from "@archlang/viewer-core";
import type { LensEntryData, WalkResult } from "@archlang/engine";
import { LifecycleSection } from "../board/lifecycle/LifecycleSection.js";
import { useLifecyclePaint } from "../board/lifecycle/useLifecycleState.js";
import { resolveAspectControl, type AspectControl } from "../workbench/languagePacks/aspectControlRegistry.js";

export type SidebarTab = "details" | "process" | "diagnostics";

export interface ConnRef {
  readonly edgeId: string;
  readonly peerId: string;
  readonly peerLabel: string;
  readonly direction: "out" | "in" | "internal";
  readonly details: readonly CallDetail[];
}

export interface Indexes {
  moduleById: ReadonlyMap<string, Module>;
  moduleParentById: ReadonlyMap<string, StableId>;
  moduleQualifiedNameById: ReadonlyMap<string, string>;
  interfaceById: ReadonlyMap<string, { iface: Interface; moduleId: string }>;
  processById: ReadonlyMap<string, Process>;
}

export interface BoardIndexes {
  connByModule: ReadonlyMap<string, readonly ConnRef[]>;
  processesByModule: ReadonlyMap<string, ReadonlySet<string>>;
  connByInterface: ReadonlyMap<string, readonly ConnRef[]>;
}

export interface SelectionRef {
  moduleId: StableId | null;
  edgeId: string | null;
}

/** Details for a selected aspect-web node (synthetic, not a real module). */
export interface AspectDetail {
  readonly key: string;
  readonly value: string;
  readonly color: string;
  readonly members: readonly { id: string; name: string; qualifiedName: string }[];
}

interface SidebarProps {
  selectedModule: Module | null;
  selectedAspect?: AspectDetail | null;
  selectedEdge?: BoardEdge | null;
  selectedInterface?: {
    iface: Interface;
    moduleId: StableId;
    qualifiedName: string;
  } | null;
  indexes?: Indexes | null;
  boardIndexes?: BoardIndexes | null;
  /** "Mentioned in" backlinks keyed by element/document name, qualified name,
   *  or `#id`. Drives the backlinks section in the module/interface panels. */
  backlinks?: Readonly<Record<string, readonly WireBacklink[]>>;
  root?: SpaceLike | null;
  focalPath?: readonly string[];
  processes?: readonly Process[];
  activeProcessId?: StableId | null;
  diagnostics: readonly Diagnostic[];
  onBlastRadius?: () => void;
  blastRadius?: readonly string[];
  activeProcess?: Process | null;
  onSelectEdge?: (edgeId: string | null) => void;
  onSelectInterface?: (interfaceId: string) => void;
  onSelectModule?: (id: StableId) => void;
  onSelectProcess?: (id: StableId | null) => void;
  onArchlangNav?: (href: string) => void;
  subprocessDescriptions?: ReadonlyMap<string, string>;
  /**
   * Activate a process AND push to nav history so the user can back
   * out. Distinct from `onSelectProcess`, which toggles state without
   * history (used for cancellation / clears).
   */
  onActivateProcess?: (id: StableId) => void;
  onBack?: () => void;
  canGoBack?: boolean;
  historyPast?: readonly SelectionRef[];
  activeTab: SidebarTab;
  walkResult?: WalkResult | null;
  activeStepIndex?: number | null;
  walkerState?: { branchSelection: Map<string, string>; expandedSubprocesses: Set<string> };
  onBranchSelect?: (nodeId: string, label: string) => void;
  onSubprocessToggle?: (nodeId: string, expanded: boolean) => void;
  onSelectStepIndex?: (i: number | null) => void;
  /** Inline-editing bundle (W1) — injected by the workbench DetailsPane when
   *  the surface is editable; absent = read-only sidebar (standalone panel). */
  editing?: ModuleEditing | null;
  /** Lens entries for the selected element (VIEWS.md §8) — resolved by the
   *  workbench DetailsPane (`useLensEntries`); absent/empty = no section. */
  lensEntries?: readonly LensEntryData[];
  /** Plugin-contributed detail sections for the current selection (e.g. OTLP
   *  telemetry). Pre-resolved + error-boundaried by the workbench DetailsPane
   *  and rendered as-is near the top of the module/edge panel — this view stays
   *  pure (no registry read). Absent for the standalone/read-only panel. */
  pluginSections?: ReactNode;
}

// Shared empty defaults — a literal `= []` in the destructure would mint a
// fresh array per render when the prop is absent and churn downstream
// memo/effect deps (see the board-gesture-performance plan for the board
// incarnation of this bug).
const EMPTY_FOCAL_PATH: readonly string[] = [];
const EMPTY_PROCESSES: readonly never[] = [];
const EMPTY_HISTORY: readonly never[] = [];

/** Best-effort clipboard write for context-menu "Copy name" actions. */
function copyText(text: string): void {
  void navigator.clipboard?.writeText(text);
}

export function Sidebar({
  selectedModule,
  selectedAspect = null,
  selectedEdge = null,
  selectedInterface = null,
  indexes = null,
  boardIndexes = null,
  backlinks,
  root = null,
  focalPath = EMPTY_FOCAL_PATH,
  processes = EMPTY_PROCESSES,
  activeProcessId = null,
  diagnostics,
  onBlastRadius,
  blastRadius,
  activeProcess = null,
  onSelectEdge,
  onSelectInterface,
  onSelectModule,
  onSelectProcess,
  onArchlangNav,
  subprocessDescriptions,
  onActivateProcess,
  onBack,
  canGoBack = false,
  historyPast = EMPTY_HISTORY,
  activeTab,
  walkResult,
  activeStepIndex,
  walkerState,
  onBranchSelect,
  onSubprocessToggle,
  onSelectStepIndex,
  editing = null,
  lensEntries,
  pluginSections,
}: SidebarProps) {
  void historyPast;

  // Auto-pick the relevant panel from current selection state. No tabs:
  // edge + module + process + empty fall through in priority order.
  // Diagnostics live in the workspace navbar now, not here.
  void activeTab;
  void diagnostics;
  const body = selectedInterface && indexes ? (
    <InterfaceDetails
      iface={selectedInterface.iface}
      moduleId={selectedInterface.moduleId}
      qualifiedName={selectedInterface.qualifiedName}
      boardIndexes={boardIndexes}
      backlinks={backlinks}
      onSelectModule={onSelectModule}
      onSelectEdge={onSelectEdge}
      onArchlangNav={onArchlangNav}
    />
  ) : selectedEdge && indexes ? (
    <EdgeDetails
      edge={selectedEdge}
      indexes={indexes}
      onSelectModule={onSelectModule}
      onSelectProcess={onSelectProcess}
      activeProcessId={activeProcessId}
      pluginSections={pluginSections}
    />
  ) : selectedAspect ? (
    <AspectDetails aspect={selectedAspect} onSelectModule={onSelectModule} />
  ) : selectedModule && indexes ? (
    <ModuleDetails
      module={selectedModule}
      indexes={indexes}
      boardIndexes={boardIndexes}
      backlinks={backlinks}
      processes={processes}
      activeProcessId={activeProcessId}
      onBlastRadius={onBlastRadius}
      blastRadius={blastRadius}
      onSelectInterface={onSelectInterface}
      onSelectEdge={onSelectEdge}
      onSelectModule={onSelectModule}
      onSelectProcess={onSelectProcess}
      onArchlangNav={onArchlangNav}
      editing={editing}
      lensEntries={lensEntries}
      pluginSections={pluginSections}
    />
  ) : activeProcessId && walkResult ? (
    <ProcessTabPanel
      processes={processes}
      activeProcess={activeProcess}
      activeProcessId={activeProcessId}
      walkResult={walkResult}
      activeStepIndex={activeStepIndex ?? null}
      walkerState={walkerState ?? { branchSelection: new Map(), expandedSubprocesses: new Set() }}
      onBranchSelect={onBranchSelect ?? (() => {})}
      onSubprocessToggle={onSubprocessToggle ?? (() => {})}
      onSelectStepIndex={onSelectStepIndex ?? (() => {})}
      onSelectProcess={onSelectProcess}
      onSelectModule={onSelectModule}
      onSelectInterface={onSelectInterface}
      onArchlangNav={onArchlangNav}
      subprocessDescriptions={subprocessDescriptions}
      onActivateProcess={onActivateProcess}
    />
  ) : (
    <SpaceOverview
      root={root}
      focalPath={focalPath}
      indexes={indexes}
      boardIndexes={boardIndexes}
      processes={processes}
      onSelectModule={onSelectModule}
      onSelectProcess={onSelectProcess}
      activeProcessId={activeProcessId}
    />
  );
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {canGoBack && onBack && (
        <div className="px-2 pt-2 -mb-2 flex">
          <BackButton
            onClick={onBack}
            label={undefined}
            depth={historyPast.length}
          />
        </div>
      )}
      {body}
    </div>
  );
}

// =============================================================================
// Aspect details — a selected aspect-web node (mini "module")
// =============================================================================

function AspectDetails({
  aspect,
  onSelectModule,
}: {
  aspect: AspectDetail;
  onSelectModule?: (id: StableId) => void;
}) {
  const { t } = useTranslation("sidebar");
  const menu = useContextMenu();
  return (
    <div className="flex-1 overflow-y-auto px-3 py-3 text-sm">
      <div className="flex items-center gap-2">
        <span
          className="inline-block w-3.5 h-3.5 rounded-xs border border-white/20 shrink-0"
          style={{ background: aspect.color }}
        />
        <span className="text-slate-100 font-semibold truncate">{aspect.value}</span>
      </div>
      <div className="mt-1 text-xs text-slate-400">
        {t("aspect.label")} · <span className="text-slate-300">{aspect.key}</span>
      </div>

      <div className="mt-4 text-xs uppercase tracking-wide text-slate-500">
        {t("aspect.members", { count: aspect.members.length })}
      </div>
      {aspect.members.length === 0 ? (
        <div className="mt-1 text-xs text-slate-500">{t("aspect.noMembers")}</div>
      ) : (
        <ul className="mt-1 space-y-0.5">
          {aspect.members.map((m) => (
            <li key={m.id}>
              <Tooltip content={m.qualifiedName}>
                <button
                  onClick={() => onSelectModule?.(m.id as StableId)}
                  onContextMenu={(e) =>
                    menu.open(e, [
                      {
                        label: t("menu.showOnBoard"),
                        onClick: () => onSelectModule?.(m.id as StableId),
                      },
                      {
                        label: t("menu.copyName"),
                        separator: true,
                        onClick: () => copyText(m.qualifiedName),
                      },
                    ])
                  }
                  className="w-full text-left rounded-sm px-2 py-1 text-slate-200 hover:bg-slate-800 truncate focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
                >
                  {m.qualifiedName}
                </button>
              </Tooltip>
            </li>
          ))}
        </ul>
      )}
      {menu.node}
    </div>
  );
}

function BackButton({
  onClick,
  label,
  depth,
}: {
  onClick?: () => void;
  label?: string;
  depth: number;
}) {
  const { t } = useTranslation("sidebar");
  const title = label ? t("back.toLabel", { label }) : t("back.plain");
  return (
    <Tooltip content={title}>
      <IconButton
        onClick={onClick}
        aria-label={title}
        className="relative"
      >
        <ChevronLeft width={16} height={16} strokeWidth={2} aria-hidden />
        {depth > 1 && <Badge count={depth} placement="corner" ringClass="ring-panel" />}
      </IconButton>
    </Tooltip>
  );
}

// =============================================================================
// Panels
// =============================================================================

function EmptyState({ message }: { message: string }) {
  return (
    <div className="p-4 text-sm text-slate-400 leading-snug">{message}</div>
  );
}

function SectionHeader({ children }: { children: ReactNode }) {
  return (
    <h3 className="text-xs font-semibold text-slate-400 uppercase mb-1.5 tracking-wide">
      {children}
    </h3>
  );
}

/**
 * A key/value entry section (module Fields / Labels) with optional inline
 * editing (W1). Read-only mode keeps the classic chip row; editing mode
 * renders one row per entry — value is click-to-edit, authored entries get a
 * remove ×, inherited ones show provenance and either a drop-backed remove
 * (fields) or a disabled explanation (aspects) — plus an add-entry row.
 */
function EditableEntrySection({
  title,
  entries,
  authoredPaths,
  edit,
  controlFor,
}: {
  title: string;
  entries: readonly { path: string; value: string }[];
  authoredPaths?: ReadonlySet<string>;
  edit: {
    set: (path: string, value: string) => void;
    remove: (path: string) => void;
    removeInherited: ((path: string) => void) | null;
    inheritedRemoveTitle: string;
  } | null;
  /** A language pack's first-class inspector editor for a matching aspect key,
   *  replacing the raw `InlineText` field when present (§language packs). */
  controlFor?: (path: string) => AspectControl | undefined;
}) {
  const { t } = useTranslation("sidebar");
  const [newPath, setNewPath] = useState("");
  const [newValue, setNewValue] = useState("");
  if (!edit) {
    if (entries.length === 0) return null;
    return (
      <div>
        <SectionHeader>{title}</SectionHeader>
        <div className="flex flex-wrap gap-1">
          {entries.map((e) => (
            <span
              key={e.path}
              className="text-xs px-2 py-0.5 rounded-sm bg-slate-700 text-slate-300"
            >
              {e.path}: {e.value}
            </span>
          ))}
        </div>
      </div>
    );
  }

  const commitNew = (): void => {
    const path = newPath.trim();
    const value = newValue.trim();
    if (!path || !value) return;
    edit.set(path, value);
    setNewPath("");
    setNewValue("");
  };

  return (
    <div>
      <SectionHeader>{title}</SectionHeader>
      <div className="space-y-0.5">
        {entries.map((e) => {
          const authored = authoredPaths?.has(e.path) ?? true;
          const removable = authored || edit.removeInherited !== null;
          const control = controlFor?.(e.path);
          return (
            <div key={e.path} className="group flex items-center gap-1.5 text-xs">
              <span
                className="text-slate-500 font-mono shrink-0"
                title={authored ? undefined : t("entries.inheritedTitle")}
              >
                {e.path}
                {!authored && <span className="text-slate-600">{t("entries.inheritedSuffix")}</span>}
              </span>
              {control ? (
                <control.Editor path={e.path} value={e.value} onCommit={(next) => edit.set(e.path, next)} />
              ) : (
                <InlineText
                  ariaLabel={t("entries.valueAria", { title, path: e.path })}
                  value={e.value}
                  className="text-slate-300 flex-1"
                  onCommit={(next) => {
                    if (next) edit.set(e.path, next);
                  }}
                />
              )}
              <Tooltip content={removable ? t("entries.remove", { path: e.path }) : edit.inheritedRemoveTitle}>
                <IconButton
                  size="xs"
                  aria-label={t("entries.remove", { path: e.path })}
                  disabled={!removable}
                  className="shrink-0 text-slate-600 opacity-0 group-hover:opacity-100"
                  onClick={() => (authored ? edit.remove(e.path) : edit.removeInherited?.(e.path))}
                >
                  <X size={11} />
                </IconButton>
              </Tooltip>
            </div>
          );
        })}
        <div className="flex items-center gap-1.5 text-xs pt-0.5">
          <Plus size={11} className="text-slate-600 shrink-0" aria-hidden />
          <input
            aria-label={t("entries.newKeyAria", { title: title.toLowerCase() })}
            className="bg-transparent border border-transparent rounded-sm px-1 w-24 font-mono text-slate-400 placeholder:text-slate-600 focus:border-slate-600 focus:bg-slate-800/(--tint-dim) focus:outline-hidden"
            placeholder={t("entries.keyPlaceholder")}
            value={newPath}
            onChange={(e) => setNewPath(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && commitNew()}
          />
          <input
            aria-label={t("entries.newValueAria", { title: title.toLowerCase() })}
            className="bg-transparent border border-transparent rounded-sm px-1 flex-1 text-slate-400 placeholder:text-slate-600 focus:border-slate-600 focus:bg-slate-800/(--tint-dim) focus:outline-hidden"
            placeholder={t("entries.valuePlaceholder")}
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && commitNew()}
            onBlur={commitNew}
          />
        </div>
      </div>
    </div>
  );
}

function ModuleChip({
  id,
  label,
  onSelect,
  active = false,
}: {
  id: StableId;
  label: string;
  onSelect?: (id: StableId) => void;
  active?: boolean;
}) {
  const { t } = useTranslation("sidebar");
  const menu = useContextMenu();
  const items: ContextMenuItem[] = [
    ...(onSelect
      ? [{ label: t("menu.showOnBoard"), onClick: () => onSelect(id) }]
      : []),
    {
      label: t("menu.copyName"),
      onClick: () => void navigator.clipboard?.writeText(label),
    },
  ];
  return (
    <>
      <Tooltip content={t("moduleChip.open", { label })}>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => onSelect?.(id)}
          onContextMenu={(e) => menu.open(e, items)}
          className={
            active ? "bg-sky-500/30 text-sky-100 ring-1 ring-sky-400/60 hover:bg-sky-500/40" : ""
          }
        >
          <span className="truncate max-w-[180px]">{label}</span>
        </Button>
      </Tooltip>
      {menu.node}
    </>
  );
}

function ProcessChip({
  id,
  label,
  active,
  onSelect,
}: {
  id: StableId;
  label: string;
  active: boolean;
  onSelect?: (id: StableId | null) => void;
}) {
  const { t } = useTranslation("sidebar");
  const menu = useContextMenu();
  const items: ContextMenuItem[] = [
    ...(onSelect
      ? [
          {
            label: active ? t("processChip.clear") : t("menu.showOnBoard"),
            onClick: () => onSelect(active ? null : id),
          },
        ]
      : []),
    {
      label: t("menu.copyName"),
      onClick: () => void navigator.clipboard?.writeText(label),
    },
  ];
  return (
    <>
      <Tooltip
        content={active ? t("processChip.clear") : t("processChip.highlight")}
      >
        <Button
          size="sm"
          variant="secondary"
          onClick={() => onSelect?.(active ? null : id)}
          onContextMenu={(e) => menu.open(e, items)}
          className={
            active ? "bg-sky-500/30 text-sky-100 ring-1 ring-sky-400/60 hover:bg-sky-500/40" : ""
          }
        >
          {label}
        </Button>
      </Tooltip>
      {menu.node}
    </>
  );
}

function ConnectionRow({
  conn,
  onSelectEdge,
  onSelectModule,
}: {
  conn: ConnRef;
  onSelectEdge?: (edgeId: string | null) => void;
  onSelectModule?: (id: StableId) => void;
}) {
  const { t } = useTranslation("sidebar");
  const menu = useContextMenu();
  const arrow =
    conn.direction === "out" ? "→" : conn.direction === "in" ? "←" : "↻";
  const tint =
    conn.direction === "internal"
      ? "text-amber-300"
      : conn.direction === "out"
        ? "text-sky-300"
        : "text-emerald-300";
  const items: ContextMenuItem[] = [
    ...(onSelectModule
      ? [
          {
            label: t("menu.open"),
            onClick: () => onSelectModule(conn.peerId),
          },
        ]
      : []),
    {
      label: t("menu.copyName"),
      onClick: () => void navigator.clipboard?.writeText(conn.peerLabel),
    },
  ];
  return (
    <div
      onContextMenu={(e) => menu.open(e, items)}
      className="flex items-center gap-1.5 rounded-sm bg-slate-900/(--tint-muted) border border-slate-700/(--tint-strong) px-2 py-1.5"
    >
      <span className={`font-mono text-xs ${tint}`}>{arrow}</span>
      <Tooltip content={t("connection.openPeer", { label: conn.peerLabel })}>
        <button
          onClick={() => onSelectModule?.(conn.peerId)}
          className="flex-1 min-w-0 text-left text-sm text-slate-100 hover:text-white truncate focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
        >
          {conn.peerLabel}
        </button>
      </Tooltip>
      <span className="text-[10px] text-slate-500 font-mono">
        ×{conn.details.length}
      </span>
      {conn.direction !== "internal" && (
        // Internal (self-call) rows carry NO board connection — the scene
        // renders them badge-only, and their ConnRef edgeId is unresolvable
        // (the bare module id). Offering "Show connection" was a dead pick
        // that nulled the module and blanked the inspector (r51).
        <Tooltip content={t("connection.showConnection")}>
          <Button
            size="xs"
            variant="secondary"
            onClick={() => onSelectEdge?.(conn.edgeId)}
            className="shrink-0 hover:bg-sky-600 hover:text-white"
          >
            {t("connection.link")}
          </Button>
        </Tooltip>
      )}
      {menu.node}
    </div>
  );
}

function LensEntryRow({ entry }: { entry: LensEntryData }) {
  const { t } = useTranslation("sidebar");
  const menu = useContextMenu();
  const valueText =
    entry.value === null ? t("module.lensEmpty") : String(entry.value);
  const items: ContextMenuItem[] = [
    {
      label: t("menu.copyName"),
      onClick: () => void navigator.clipboard?.writeText(entry.title),
    },
    ...(entry.value === null
      ? []
      : [
          {
            label: t("menu.copyValue"),
            onClick: () => void navigator.clipboard?.writeText(valueText),
          },
        ]),
  ];
  return (
    <div
      data-lens-entry={entry.name}
      onContextMenu={(e) => menu.open(e, items)}
      className="flex items-baseline justify-between gap-2 text-sm"
    >
      <span className="text-slate-400">{entry.title}</span>
      <span className="text-slate-200 text-right break-all">{valueText}</span>
      {menu.node}
    </div>
  );
}

function InterfaceRow({
  iface,
  ifaceKey,
  usage,
  onSelectInterface,
  onArchlangNav,
}: {
  iface: Interface;
  ifaceKey: string;
  usage: number;
  onSelectInterface?: (interfaceId: string) => void;
  onArchlangNav?: (href: string) => void;
}) {
  const { t } = useTranslation("sidebar");
  const menu = useContextMenu();
  const items: ContextMenuItem[] = [
    ...(onSelectInterface
      ? [{ label: t("menu.open"), onClick: () => onSelectInterface(ifaceKey) }]
      : []),
    {
      label: t("menu.copyName"),
      onClick: () =>
        void navigator.clipboard?.writeText(interfaceDisplayName(iface)),
    },
  ];
  return (
    <div
      onContextMenu={(e) => menu.open(e, items)}
      className="rounded-sm bg-slate-700/(--tint-muted) hover:bg-slate-700 hover:ring-1 hover:ring-sky-500/50 transition"
    >
      <Tooltip content={t("module.openInterface")}>
        <button
          onClick={() => onSelectInterface?.(ifaceKey)}
          className="w-full text-left text-sm p-2 flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
        >
          <span
            className={
              "truncate flex-1 " +
              (isAnonymousInterface(iface)
                ? "italic text-slate-400"
                : "text-white")
            }
          >
            {interfaceDisplayName(iface)}
          </span>
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded ${
              iface.typeName === "event" ||
              iface.typeName === "stream" ||
              iface.typeName === "async"
                ? "bg-purple-900/50 text-purple-300"
                : "bg-blue-900/50 text-blue-300"
            }`}
          >
            {iface.typeName}
          </span>
          <span className="text-[10px] text-slate-500 font-mono w-8 text-right">
            {usage ? `×${usage}` : ""}
          </span>
        </button>
      </Tooltip>
      {iface.description && (
        <div
          className="arch-desc text-xs text-slate-400 px-2 pb-2 pt-0"
          onClick={
            onArchlangNav
              ? (e) => handleDescriptionClick(e, onArchlangNav)
              : undefined
          }
          dangerouslySetInnerHTML={{
            __html: renderDescriptionHtml(iface.description),
          }}
        />
      )}
      {menu.node}
    </div>
  );
}

export function ModuleDetails({
  module: selectedModule,
  indexes,
  boardIndexes,
  backlinks,
  processes,
  activeProcessId,
  onBlastRadius,
  blastRadius,
  onSelectInterface,
  onSelectEdge,
  onSelectModule,
  onSelectProcess,
  onArchlangNav,
  editing = null,
  lensEntries,
  pluginSections,
}: {
  module: Module;
  indexes: Indexes;
  boardIndexes: BoardIndexes | null;
  backlinks?: Readonly<Record<string, readonly WireBacklink[]>>;
  processes: readonly Process[];
  activeProcessId: StableId | null;
  onBlastRadius?: () => void;
  blastRadius?: readonly string[];
  onSelectInterface?: (interfaceId: string) => void;
  onSelectEdge?: (edgeId: string | null) => void;
  onSelectModule?: (id: StableId) => void;
  onSelectProcess?: (id: StableId | null) => void;
  onArchlangNav?: (href: string) => void;
  editing?: ModuleEditing | null;
  lensEntries?: readonly LensEntryData[];
  pluginSections?: ReactNode;
}) {
  const { t } = useTranslation("sidebar");
  const moduleId = selectedModule.id ?? selectedModule.name;
  // Inline-edit feedback: one transient message slot for the whole panel
  // (stale compile / refused op). Cleared on the next successful commit.
  const [editNote, setEditNote] = useState<string | null>(null);
  const report = (r: MutationResult): void => {
    if (r.status === "applied") setEditNote(null);
    else if (r.status === "stale") setEditNote(t("module.noteStale"));
    else if (r.status === "refused") setEditNote(r.message);
    else setEditNote(t("module.noteNotEditable"));
  };
  const [editingDescription, setEditingDescription] = useState(false);
  const edit = editing?.editable ? editing : null;
  const moduleTeam = getTeam(selectedModule);
  const moduleInterfaces = flattenInterfaces(selectedModule);
  const parentId = indexes.moduleParentById.get(moduleId);
  const parent = parentId ? indexes.moduleById.get(parentId) : undefined;
  const qualifiedName =
    indexes.moduleQualifiedNameById.get(moduleId) ?? selectedModule.name;
  const moduleBacklinks = lookupBacklinks(backlinks, [
    selectedModule.id ? `#${selectedModule.id}` : undefined,
    qualifiedName,
    selectedModule.name,
  ]);

  // The `widget` field-tree is internal board-render config (widget type / icon /
  // colour / size) — not architecture data, and its object-valued size renders as
  // "[object Object]". Keep it out of the detail's FIELDS list.
  const visibleFields = selectedModule.fields.filter((f) => f.path[0] !== "widget");
  const lifecyclePaint = useLifecyclePaint();

  const allConns = boardIndexes?.connByModule.get(moduleId) ?? [];
  const outgoing = allConns.filter((c) => c.direction === "out");
  const incoming = allConns.filter((c) => c.direction === "in");
  const internal = allConns.filter((c) => c.direction === "internal");

  const processIds = boardIndexes?.processesByModule.get(moduleId);
  const involvedProcesses = processIds
    ? processes.filter((p) => processIds.has(processKey(p)))
    : [];

  return (
    <div className="p-4 overflow-y-auto space-y-4">
      <div>
        <div className="text-xs uppercase text-slate-500 mb-1">{t("module.heading")}</div>
        <h2 className="text-lg font-semibold text-white flex items-center gap-2 flex-wrap">
          {edit?.renameEnabled ? (
            <InlineText
              ariaLabel={t("module.nameAria")}
              value={selectedModule.name}
              className={
                "text-lg font-semibold text-white flex-1 " +
                (selectedModule.unnamed ? "italic text-slate-200" : "")
              }
              onCommit={(next) => {
                if (!next) return;
                void edit.rename(next).then(report);
              }}
            />
          ) : (
            <span className={selectedModule.unnamed ? "italic text-slate-200" : undefined}>
              {selectedModule.name}
            </span>
          )}
          {selectedModule.unnamed && (
            <span
              className="text-[10px] font-medium not-italic px-1.5 py-0.5 rounded-sm bg-slate-700/(--tint-dim) text-slate-400"
              title={t("module.derivedTitle")}
            >
              {t("module.derived")}
            </span>
          )}
        </h2>
        {qualifiedName !== selectedModule.name && (
          <div className="text-[11px] text-slate-500 font-mono">
            {qualifiedName}
          </div>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          {(selectedModule.typeName || edit) && (
            <span className="text-slate-400">
              <span className="text-slate-500">{t("module.kind")}</span>{" "}
              {edit ? (
                <InlineText
                  ariaLabel={t("module.kindAria")}
                  value={selectedModule.typeName ?? "module"}
                  className="text-slate-200 w-32"
                  options={edit.typeOptions}
                  onCommit={(next) => {
                    if (next) report(edit.setType(next));
                  }}
                />
              ) : (
                <span className="text-slate-200">{selectedModule.typeName}</span>
              )}
            </span>
          )}
          {moduleTeam && (
            <span className="text-slate-400 flex items-center gap-1.5">
              <span
                aria-hidden
                className="inline-block rounded-full border border-slate-600"
                style={{
                  width: 9,
                  height: 9,
                  // The palette's value-stable hash — the same colour the board
                  // gives this team, so inspector and diagram cannot disagree.
                  background: aspectColorFor(moduleTeam),
                }}
              />
              <span>{moduleTeam}</span>
            </span>
          )}
          {parent && (
            <span className="text-slate-400">
              <span className="text-slate-500">{t("module.in")}</span>{" "}
              <button
                className="text-sky-300 hover:text-sky-200 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
                onClick={() => onSelectModule?.(parent.id ?? parent.name)}
              >
                {parent.name}
              </button>
            </span>
          )}
        </div>
      </div>

      {pluginSections}

      {editNote && (
        <div className="text-xs text-amber-400/90" role="status">
          {editNote}
        </div>
      )}

      {edit && editingDescription && edit.description.editable ? (
        <InlineTextarea
          ariaLabel={t("module.descriptionAria")}
          value={edit.description.text}
          placeholder={t("module.descriptionPlaceholder")}
          onCommit={(next) => {
            setEditingDescription(false);
            if (next !== edit.description.text) report(edit.setDescription(next));
          }}
        />
      ) : selectedModule.description ? (
        <div className="group relative">
          <div
            className="arch-desc text-sm text-slate-300 leading-snug"
            onClick={
              onArchlangNav
                ? (e) => handleDescriptionClick(e, onArchlangNav)
                : undefined
            }
            dangerouslySetInnerHTML={{
              __html: renderDescriptionHtml(selectedModule.description),
            }}
          />
          {edit && (
            <Tooltip
              content={
                edit.description.editable
                  ? t("module.editDescription")
                  : t("module.editDescriptionMultiple")
              }
            >
              <IconButton
                size="xs"
                aria-label={t("module.editDescription")}
                disabled={!edit.description.editable}
                className="absolute top-0 right-0 text-slate-500 opacity-0 group-hover:opacity-100"
                onClick={() => setEditingDescription(true)}
              >
                <Pencil size={12} />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ) : edit?.description.editable ? (
        <Button
          size="xs"
          variant="ghost"
          className="self-start px-0 text-slate-500"
          onClick={() => setEditingDescription(true)}
        >
          {t("module.addDescription")}
        </Button>
      ) : null}

      <Backlinks backlinks={moduleBacklinks} onNavigate={onArchlangNav} />

      {(visibleFields.length > 0 || edit) && (
        <EditableEntrySection
          title={t("module.fields")}
          entries={visibleFields.map((f) => {
            const path = f.path.join(".");
            // For an AUTHORED field, show what the author wrote: a getter or
            // cast must read back as the rule, not the value it resolved to on
            // this element, or saving the row would freeze it (spec §4.3.2).
            const authored = edit?.authoredFieldText.get(path);
            return {
              path,
              value: authored ?? String((f.value as { text?: string })?.text ?? f.value ?? ""),
            };
          })}
          authoredPaths={edit?.authoredFieldPaths}
          edit={
            edit
              ? {
                  set: (path, value) => report(edit.setField(path, value)),
                  remove: (path) => report(edit.removeField(path)),
                  removeInherited: (path) => report(edit.removeField(path)),
                  inheritedRemoveTitle: t("module.inheritedRemoveField"),
                }
              : null
          }
        />
      )}

      {(selectedModule.aspects.length > 0 || edit) && (
        <EditableEntrySection
          title={t("module.labels")}
          entries={selectedModule.aspects.map((l) => ({
            path: l.path.join("."),
            value: String((l.value as { text?: string })?.text ?? l.value ?? ""),
          }))}
          authoredPaths={edit?.authoredAspectPaths}
          controlFor={resolveAspectControl}
          edit={
            edit
              ? {
                  set: (path, value) => report(edit.setAspect(path, value)),
                  remove: (path) => report(edit.removeAspect(path)),
                  removeInherited: null, // inherited-aspect removal is a W1+ decision
                  inheritedRemoveTitle: t("module.inheritedRemoveAspect"),
                }
              : null
          }
        />
      )}

      <LifecycleSection
        decorations={selectedModule.decorations}
        // `moduleKey`, not `moduleId`: paint is published under the board's own
        // key — the stable `#id`, else the QUALIFIED path. `moduleId` falls back
        // to the bare name, so a nested module without an `#id` looked up
        // `Nested` against a map holding `Host.Nested`, missed, and showed no
        // state at all while its card on the board was greyed and struck.
        state={lifecyclePaint?.get(moduleKey(selectedModule))?.state}
      />

      {lensEntries && lensEntries.length > 0 && (
        <div>
          <SectionHeader>{t("module.lens")}</SectionHeader>
          <div className="space-y-0.5">
            {lensEntries.map((e) => (
              <LensEntryRow key={e.name} entry={e} />
            ))}
          </div>
        </div>
      )}

      {selectedModule.children.length > 0 && (
        <div>
          <SectionHeader>
            {t("module.children", { count: selectedModule.children.length })}
          </SectionHeader>
          <div className="flex flex-wrap gap-1">
            {selectedModule.children.map((c) => (
              <ModuleChip
                key={c.id ?? c.name}
                id={c.id ?? c.name}
                label={c.name}
                onSelect={onSelectModule}
              />
            ))}
          </div>
        </div>
      )}

      {moduleInterfaces.length > 0 && (
        <div>
          <SectionHeader>
            {t("module.interfaces", { count: moduleInterfaces.length })}
          </SectionHeader>
          <div className="space-y-1">
            {moduleInterfaces.map((iface) => {
              const ifaceKey = `${qualifiedName}.${iface.name}`;
              const usage =
                boardIndexes?.connByInterface.get(ifaceKey)?.length ?? 0;
              return (
                <InterfaceRow
                  key={iface.name}
                  iface={iface}
                  ifaceKey={ifaceKey}
                  usage={usage}
                  onSelectInterface={onSelectInterface}
                  onArchlangNav={onArchlangNav}
                />
              );
            })}
          </div>
        </div>
      )}

      {(outgoing.length > 0 || incoming.length > 0 || internal.length > 0) && (
        <div>
          <SectionHeader>
            {t("module.connections", { count: outgoing.length + incoming.length + internal.length })}
          </SectionHeader>
          {outgoing.length > 0 && (
            <div className="mb-2">
              <div className="text-[10px] uppercase text-sky-400/80 mb-1">
                {t("module.callsOut", { count: outgoing.length })}
              </div>
              <div className="space-y-1">
                {outgoing.map((c, i) => (
                  <ConnectionRow
                    key={`out-${c.edgeId}-${i}`}
                    conn={c}
                    onSelectEdge={onSelectEdge}
                    onSelectModule={onSelectModule}
                  />
                ))}
              </div>
            </div>
          )}
          {incoming.length > 0 && (
            <div className="mb-2">
              <div className="text-[10px] uppercase text-emerald-400/80 mb-1">
                {t("module.calledBy", { count: incoming.length })}
              </div>
              <div className="space-y-1">
                {incoming.map((c, i) => (
                  <ConnectionRow
                    key={`in-${c.edgeId}-${i}`}
                    conn={c}
                    onSelectEdge={onSelectEdge}
                    onSelectModule={onSelectModule}
                  />
                ))}
              </div>
            </div>
          )}
          {internal.length > 0 && (
            <div>
              <div className="text-[10px] uppercase text-amber-400/80 mb-1">
                {t("module.internal", { count: internal.length })}
              </div>
              <div className="space-y-1">
                {internal.map((c, i) => (
                  <ConnectionRow
                    key={`int-${c.edgeId}-${i}`}
                    conn={c}
                    onSelectEdge={onSelectEdge}
                    onSelectModule={onSelectModule}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {involvedProcesses.length > 0 && (
        <div>
          <SectionHeader>
            {t("module.processes", { count: involvedProcesses.length })}
          </SectionHeader>
          <div className="flex flex-wrap gap-1">
            {involvedProcesses.map((p) => {
              const pid = processKey(p);
              return (
                <ProcessChip
                  key={pid}
                  id={pid}
                  label={p.name}
                  active={pid === activeProcessId}
                  onSelect={onSelectProcess}
                />
              );
            })}
          </div>
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <Button variant="primary" onClick={onBlastRadius} className="flex-1">
          {t("module.showBlastRadius")}
        </Button>
      </div>

      {blastRadius && blastRadius.length > 0 && (
        <div className="p-2 rounded-sm bg-yellow-900/20 border border-yellow-700">
          <div className="text-xs text-yellow-400 mb-1">
            {t("module.blastRadius", { count: blastRadius.length })}
          </div>
          <div className="flex flex-wrap gap-1 mt-1">
            {blastRadius.map((id) => {
              const m = indexes.moduleById.get(id);
              return (
                <ModuleChip
                  key={id}
                  id={id}
                  label={m?.name ?? id}
                  onSelect={onSelectModule}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function DiagnosticsPanel({
  diagnostics,
}: {
  diagnostics: readonly Diagnostic[];
}) {
  const { t } = useTranslation("sidebar");
  return (
    <div className="flex-1 overflow-auto p-4">
      <h2 className="text-sm font-semibold text-slate-400 uppercase mb-3">
        {t("diagnostics.heading", { count: diagnostics.length })}
      </h2>
      {diagnostics.length === 0 ? (
        <div className="text-slate-500 text-sm">{t("diagnostics.noIssues")}</div>
      ) : (
        <div className="space-y-2">
          {diagnostics.map((d, i) => (
            <DiagnosticBadge key={i} diagnostic={d} />
          ))}
        </div>
      )}
    </div>
  );
}

function DiagnosticBadge({ diagnostic }: { diagnostic: Diagnostic }) {
  const { locale } = useLocale();
  const { t } = useTranslation("sidebar");
  const menu = useContextMenu();
  const message = diagnosticText(diagnostic, locale);
  const colors = {
    error: "bg-red-900/50 text-red-300 border-red-700",
    warning: "bg-yellow-900/50 text-yellow-300 border-yellow-700",
    info: "bg-amber-900/40 text-amber-200 border-amber-700/70",
    hint: "bg-slate-700 text-slate-300 border-slate-600",
  };
  const items: ContextMenuItem[] = [
    {
      label: t("menu.copyMessage"),
      onClick: () => void navigator.clipboard?.writeText(message),
    },
  ];

  return (
    <div
      onContextMenu={(e) => menu.open(e, items)}
      className={`p-2 rounded-sm border ${colors[diagnostic.severity]}`}
    >
      <div className="text-xs font-medium uppercase opacity-60">
        {diagnostic.severity}
      </div>
      <div className="text-sm">{message}</div>
      {diagnostic.code && (
        <div className="text-xs opacity-50 mt-1">{diagnostic.code}</div>
      )}
      {menu.node}
    </div>
  );
}

export function InterfaceDetails({
  iface,
  moduleId,
  qualifiedName,
  boardIndexes,
  backlinks,
  onSelectModule,
  onSelectEdge,
  onArchlangNav,
}: {
  iface: Interface;
  moduleId: StableId;
  qualifiedName: string;
  boardIndexes: BoardIndexes | null;
  backlinks?: Readonly<Record<string, readonly WireBacklink[]>>;
  onSelectModule?: (id: StableId) => void;
  onSelectEdge?: (edgeId: string | null) => void;
  onArchlangNav?: (href: string) => void;
}) {
  const { t } = useTranslation("sidebar");
  const menu = useContextMenu();
  // Owner module is everything left of the final `.` in the qualified path.
  const ownerName = qualifiedName.split(".").slice(0, -1).join(".");
  const conns = boardIndexes?.connByInterface.get(qualifiedName) ?? [];
  const ifaceBacklinks = lookupBacklinks(backlinks, [qualifiedName, iface.name]);
  const async = iface.typeName === "async";
  // Hide the inherited `widget.*` styling fields — they're rendering
  // config, not part of the interface's logical surface.
  const surfaceFields = iface.fields.filter((f) => f.path[0] !== "widget");

  return (
    <div className="p-4 overflow-y-auto space-y-4">
      <div>
        <div className="text-xs uppercase text-slate-500 mb-1">{t("interface.heading")}</div>
        <h2 className="text-lg font-semibold text-white flex items-center gap-2 flex-wrap">
          <span className={isAnonymousInterface(iface) ? "italic text-slate-300" : undefined}>
            {interfaceDisplayName(iface)}
          </span>
          <span
            className={
              "text-[11px] px-1.5 py-0.5 rounded-sm font-medium " +
              (async
                ? "bg-purple-900/50 text-purple-300"
                : "bg-blue-900/50 text-blue-300")
            }
          >
            {iface.typeName}
          </span>
        </h2>
        {ownerName && (
          <div className="text-[11px] text-slate-500 mt-1">
            {t("interface.on")}{" "}
            <button
              className="text-sky-300 hover:text-sky-200 underline-offset-2 hover:underline font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
              onClick={() => onSelectModule?.(moduleId)}
            >
              {ownerName}
            </button>
          </div>
        )}
      </div>

      {iface.description && (
        <div
          className="arch-desc text-sm text-slate-300 leading-snug"
          onClick={
            onArchlangNav
              ? (e) => handleDescriptionClick(e, onArchlangNav)
              : undefined
          }
          dangerouslySetInnerHTML={{
            __html: renderDescriptionHtml(iface.description),
          }}
        />
      )}

      <Backlinks backlinks={ifaceBacklinks} onNavigate={onArchlangNav} />

      {iface.aspects.length > 0 && (
        <div>
          <SectionHeader>{t("interface.labels")}</SectionHeader>
          <div className="flex flex-wrap gap-1">
            {iface.aspects.map((l) => (
              <span
                key={l.path.join(".")}
                className="text-xs px-2 py-0.5 rounded-sm bg-slate-700 text-slate-300"
              >
                {l.path.join(".")}:{" "}
                {String((l.value as { text?: string })?.text ?? l.value)}
              </span>
            ))}
          </div>
        </div>
      )}

      {surfaceFields.length > 0 && (
        <div>
          <SectionHeader>{t("interface.fields")}</SectionHeader>
          <div className="flex flex-wrap gap-1">
            {surfaceFields.map((f) => (
              <span
                key={f.path.join(".")}
                className="text-xs px-2 py-0.5 rounded-sm bg-slate-700 text-slate-300"
              >
                {f.path.join(".")}:{" "}
                {String((f.value as { text?: string })?.text ?? f.value)}
              </span>
            ))}
          </div>
        </div>
      )}

      <div>
        <SectionHeader>{t("interface.usedBy", { count: conns.length })}</SectionHeader>
        {conns.length === 0 ? (
          <div className="text-xs text-slate-500 italic">
            {t("interface.noCalls")}
          </div>
        ) : (
          <ul className="space-y-1">
            {conns.map((c, i) => (
              <li key={`${c.edgeId}-${i}`}>
                <Tooltip content={t("interface.openConnection")}>
                  <button
                    onClick={() => onSelectEdge?.(c.edgeId)}
                    onContextMenu={(e) =>
                      menu.open(e, [
                        {
                          label: t("menu.open"),
                          onClick: () => onSelectEdge?.(c.edgeId),
                        },
                        {
                          label: t("menu.copyName"),
                          separator: true,
                          onClick: () => copyText(c.peerLabel),
                        },
                      ])
                    }
                    className="w-full text-left text-sm p-2 rounded-sm bg-slate-700/(--tint-muted) hover:bg-slate-700 hover:ring-1 hover:ring-sky-500/50 transition flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
                  >
                    <span className="text-[10px] text-slate-500 uppercase w-10 shrink-0">
                      {c.direction}
                    </span>
                    <span className="text-slate-200 truncate flex-1">
                      {c.peerLabel}
                    </span>
                  </button>
                </Tooltip>
              </li>
            ))}
          </ul>
        )}
      </div>
      {menu.node}
    </div>
  );
}

export function EdgeDetails({
  edge,
  indexes,
  onSelectModule,
  onSelectProcess,
  activeProcessId,
  pluginSections,
}: {
  edge: BoardEdge;
  indexes: Indexes;
  onSelectModule?: (id: StableId) => void;
  onSelectProcess?: (id: StableId | null) => void;
  activeProcessId: StableId | null;
  pluginSections?: ReactNode;
}) {
  const { t } = useTranslation("sidebar");
  const menu = useContextMenu();
  const fromMod = indexes.moduleById.get(edge.from);
  const toMod = indexes.moduleById.get(edge.to);
  const count = edge.details.length;

  // Aggregate processes that contributed at least one call to this edge.
  const processCounts = new Map<string, number>();
  for (const d of edge.details) {
    if (!d.processId) continue;
    processCounts.set(d.processId, (processCounts.get(d.processId) ?? 0) + 1);
  }

  // Group call details by (fromInterface, toInterface) for a tidier view.
  interface Grouped {
    fromPath: string;
    toPath: string;
    fromInterface?: { iface: Interface; moduleId: string };
    toInterface?: { iface: Interface; moduleId: string };
    calls: CallDetail[];
  }
  const groups = new Map<string, Grouped>();
  for (const d of edge.details) {
    const key = `${d.fromPath}→${d.toPath}`;
    const fromI = d.fromInterfaceId
      ? indexes.interfaceById.get(d.fromInterfaceId)
      : undefined;
    const toI = d.toInterfaceId
      ? indexes.interfaceById.get(d.toInterfaceId)
      : undefined;
    const g = groups.get(key);
    if (g) g.calls.push(d);
    else
      groups.set(key, {
        fromPath: d.fromPath,
        toPath: d.toPath,
        fromInterface: fromI,
        toInterface: toI,
        calls: [d],
      });
  }

  return (
    <div className="p-4 overflow-y-auto space-y-4">
      <div>
        <div className="text-xs uppercase text-slate-500 mb-1">{t("edge.heading")}</div>
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Tooltip content={t("edge.openModule", { name: fromMod?.name ?? edge.from })}>
            <button
              onClick={() => onSelectModule?.(edge.from)}
              onContextMenu={(e) =>
                menu.open(e, [
                  {
                    label: t("menu.showOnBoard"),
                    onClick: () => onSelectModule?.(edge.from),
                  },
                  {
                    label: t("menu.copyName"),
                    separator: true,
                    onClick: () => copyText(fromMod?.name ?? edge.from),
                  },
                ])
              }
              className="text-sky-200 hover:text-white truncate underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
            >
              {fromMod?.name ?? edge.from}
            </button>
          </Tooltip>
          <span className="text-sky-400">→</span>
          <Tooltip content={t("edge.openModule", { name: toMod?.name ?? edge.to })}>
            <button
              onClick={() => onSelectModule?.(edge.to)}
              onContextMenu={(e) =>
                menu.open(e, [
                  {
                    label: t("menu.showOnBoard"),
                    onClick: () => onSelectModule?.(edge.to),
                  },
                  {
                    label: t("menu.copyName"),
                    separator: true,
                    onClick: () => copyText(toMod?.name ?? edge.to),
                  },
                ])
              }
              className="text-sky-200 hover:text-white truncate underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
            >
              {toMod?.name ?? edge.to}
            </button>
          </Tooltip>
        </h2>
        <div className="text-xs text-slate-400 mt-1">
          {t("edge.callSummary", { count })}
          {groups.size !== count && t("edge.interfacePairs", { count: groups.size })}
        </div>
      </div>

      {pluginSections}

      {processCounts.size > 0 && (
        <div>
          <SectionHeader>
            {t("edge.processes", { count: processCounts.size })}
          </SectionHeader>
          <div className="flex flex-wrap gap-1">
            {[...processCounts].map(([pid, c]) => {
              const p = indexes.processById.get(pid);
              return (
                <ProcessChip
                  key={pid}
                  id={pid}
                  label={`${p?.name ?? pid} · ${c}`}
                  active={pid === activeProcessId}
                  onSelect={onSelectProcess}
                />
              );
            })}
          </div>
        </div>
      )}

      <div>
        <SectionHeader>{t("edge.calls")}</SectionHeader>
        <ul className="space-y-2">
          {[...groups.values()].map((g, i) => (
            <li
              key={`${g.fromPath}-${g.toPath}-${i}`}
              className="rounded-sm border border-slate-700 bg-slate-900/(--tint-muted) p-2"
            >
              <div className="flex items-baseline gap-1.5 text-sm">
                <span className="text-slate-300 truncate" title={g.fromPath}>
                  {g.fromPath}
                </span>
                {g.fromInterface && (
                  <InterfaceBadge iface={g.fromInterface.iface} />
                )}
              </div>
              <div className="text-xs text-slate-500 my-1">{t("edge.callsVerb")}</div>
              <div className="flex items-baseline gap-1.5 text-sm">
                <span className="text-slate-100 truncate" title={g.toPath}>
                  {g.toPath}
                </span>
                {g.toInterface && (
                  <InterfaceBadge iface={g.toInterface.iface} />
                )}
              </div>
              {g.calls.length > 1 && (
                <div className="mt-1 text-[10px] text-slate-500">
                  {t("edge.occurrences", { count: g.calls.length })}
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
      {menu.node}
    </div>
  );
}

function InterfaceBadge({ iface }: { iface: Interface }) {
  return (
    <span
      className={
        "text-[10px] px-1.5 py-0.5 rounded-sm whitespace-nowrap " +
        (iface.typeName === "async"
          ? "bg-purple-900/50 text-purple-300"
          : "bg-blue-900/50 text-blue-300")
      }
    >
      <span className={isAnonymousInterface(iface) ? "italic" : undefined}>
        {interfaceDisplayName(iface)}
      </span>{" "}
      · {iface.typeName}
    </span>
  );
}

// =============================================================================
// Space overview — what's shown when nothing is selected.
// =============================================================================

function SpaceOverview({
  root,
  focalPath,
  indexes,
  boardIndexes,
  processes,
  onSelectModule,
  onSelectProcess,
  activeProcessId,
}: {
  root: SpaceLike | null;
  focalPath: readonly string[];
  indexes: Indexes | null;
  boardIndexes: BoardIndexes | null;
  processes: readonly Process[];
  onSelectModule?: (id: StableId) => void;
  onSelectProcess?: (id: StableId | null) => void;
  activeProcessId: StableId | null;
}) {
  const { t } = useTranslation("sidebar");
  const menu = useContextMenu();
  if (!root) {
    return (
      <EmptyState message={t("space.empty")} />
    );
  }
  // `root` is the board source (lazy on the live board). The focal space + its
  // ancestor chain come from the source; full-subtree counts come from the
  // MODEL (indexes) since the lazy board only solves to a preview depth.
  const source = asSource(root);
  const rootSpace = source.at([]);
  const focal = source.at(focalPath);
  const isRoot = focalPath.length <= 1 || focal.id === rootSpace.id;
  const path: BoardSpace[] = walkFocal(source, focalPath).map((s) => s.space);
  if (path.length === 0) path.push(rootSpace);

  const items = focal.items;
  const edges = focal.edges ?? [];
  const totalDeepModules = deepModuleCount(focalPath, indexes);

  // Processes that touch any module under this focal space (model-derived).
  const idsUnder = deepModuleIds(focalPath, indexes);
  const involvedProcessIds = new Set<string>();
  if (boardIndexes) {
    for (const id of idsUnder) {
      const set = boardIndexes.processesByModule.get(id);
      if (!set) continue;
      for (const pid of set) involvedProcessIds.add(pid);
    }
  }
  const involvedProcesses = processes.filter((p) =>
    involvedProcessIds.has(processKey(p)),
  );

  return (
    <div className="p-4 overflow-y-auto space-y-4">
      <div>
        <div className="text-xs uppercase text-slate-500 mb-1">
          {isRoot ? t("space.workspace") : t("space.space")}
        </div>
        <h2 className="text-lg font-semibold text-white">
          {labelForSpace(focal, indexes, isRoot, t("space.root"))}
        </h2>
        {!isRoot && path.length > 1 && (
          <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap items-center gap-1">
            {path.map((s, i) => (
              <span key={s.id} className="flex items-center gap-1">
                <span className="text-slate-400">
                  {labelForSpace(s, indexes, i === 0, t("space.root"))}
                </span>
                {i < path.length - 1 && (
                  <span className="text-slate-600">›</span>
                )}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Stat label={t("space.statItems")} value={items.length} />
        <Stat label={t("space.statEdges")} value={edges.length} />
        <Stat label={t("space.statModules")} value={totalDeepModules} />
      </div>

      {items.length > 0 && (
        <div>
          <SectionHeader>
            {isRoot
              ? t("space.rootModules", { count: items.length })
              : t("space.modulesHere", { count: items.length })}
          </SectionHeader>
          <div className="space-y-1">
            {items.map((it) => {
              const mod = indexes?.moduleById.get(it.id);
              const conns =
                boardIndexes?.connByModule.get(it.id)?.length ?? 0;
              return (
                <Tooltip key={it.id} content={t("space.openModule", { label: it.label })}>
                  <button
                    onClick={() => onSelectModule?.(it.id)}
                    onContextMenu={(e) =>
                      menu.open(e, [
                        {
                          label: t("menu.showOnBoard"),
                          onClick: () => onSelectModule?.(it.id),
                        },
                        {
                          label: t("menu.copyName"),
                          separator: true,
                          onClick: () => copyText(it.label),
                        },
                      ])
                    }
                    className="w-full text-left rounded-sm bg-slate-900/(--tint-muted) border border-slate-700/(--tint-strong) px-2 py-1.5 hover:bg-slate-700 hover:border-sky-500/50 transition flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
                  >
                    <span className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">
                        {it.label}
                      </div>
                      {mod?.typeName && (
                        <div className="text-[10px] text-slate-500 truncate">
                          {mod.typeName}
                          {mod.children.length > 0 &&
                            ` · ${t("space.childCount", { count: mod.children.length })}`}
                        </div>
                      )}
                    </span>
                    {conns > 0 && (
                      <span className="text-[10px] text-slate-500 font-mono">
                        {t("space.linkCount", { count: conns })}
                      </span>
                    )}
                  </button>
                </Tooltip>
              );
            })}
          </div>
        </div>
      )}

      {involvedProcesses.length > 0 && (
        <div>
          <SectionHeader>
            {t("space.processes", { count: involvedProcesses.length })}
          </SectionHeader>
          <div className="flex flex-wrap gap-1">
            {involvedProcesses.map((p) => {
              const pid = processKey(p);
              return (
                <ProcessChip
                  key={pid}
                  id={pid}
                  label={p.name}
                  active={pid === activeProcessId}
                  onSelect={onSelectProcess}
                />
              );
            })}
          </div>
        </div>
      )}

      <div className="text-xs text-slate-500 leading-snug pt-2 border-t border-slate-800">
        {t("space.tip")}
      </div>
      {menu.node}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-sm border border-slate-700/(--tint-strong) bg-slate-900/(--tint-muted) px-2 py-1.5">
      <div className="text-[10px] uppercase text-slate-500">{label}</div>
      <div className="text-base font-semibold text-slate-100 tabular-nums">
        {value}
      </div>
    </div>
  );
}

function labelForSpace(
  space: BoardSpace,
  indexes: Indexes | null,
  isRoot: boolean,
  rootLabel: string,
): string {
  if (isRoot) return rootLabel;
  const m = indexes?.moduleById.get(space.id);
  return m?.name ?? space.id;
}

/** Module id of the focal space (its last `"id::space"` segment), or null at
 *  root (the root space is not a module). */
function focalModuleId(focalPath: readonly string[]): string | null {
  if (focalPath.length <= 1) return null;
  const last = focalPath[focalPath.length - 1]!;
  return last.endsWith("::space") ? last.slice(0, -"::space".length) : last;
}

/** Count of all modules under the focal — from the MODEL tree, so it stays
 *  correct on the lazily-solved board (which only materialises a preview
 *  depth). At root, every module is "under" the workspace. */
function deepModuleCount(focalPath: readonly string[], indexes: Indexes | null): number {
  if (!indexes) return 0;
  const fid = focalModuleId(focalPath);
  if (!fid) return indexes.moduleById.size;
  const m = indexes.moduleById.get(fid);
  if (!m) return 0;
  let n = 0;
  const walk = (mod: typeof m) => {
    for (const c of mod.children) {
      n++;
      walk(c);
    }
  };
  walk(m);
  return n;
}

/** Module keys under the focal (model-derived), matching the keys
 *  `boardIndexes.processesByModule` is keyed by. */
function deepModuleIds(focalPath: readonly string[], indexes: Indexes | null): string[] {
  if (!indexes) return [];
  const fid = focalModuleId(focalPath);
  if (!fid) return [...indexes.moduleById.keys()];
  const m = indexes.moduleById.get(fid);
  if (!m) return [];
  const out: string[] = [];
  const walk = (mod: typeof m) => {
    for (const c of mod.children) {
      out.push(moduleKey(c));
      walk(c);
    }
  };
  walk(m);
  return out;
}

// =============================================================================
// Process tab — list of all processes + active flow.
// =============================================================================

function ProcessTabPanel({
  processes,
  activeProcess,
  activeProcessId,
  walkResult,
  activeStepIndex,
  walkerState,
  onBranchSelect,
  onSubprocessToggle,
  onSelectStepIndex,
  onSelectProcess,
  onSelectModule,
  onSelectInterface,
  onArchlangNav,
  subprocessDescriptions,
  onActivateProcess,
}: {
  processes: readonly Process[];
  activeProcess: Process | null;
  activeProcessId: StableId | null;
  walkResult: WalkResult | null;
  activeStepIndex: number | null;
  walkerState: { branchSelection: Map<string, string>; expandedSubprocesses: Set<string> };
  onBranchSelect: (nodeId: string, label: string) => void;
  onSubprocessToggle: (nodeId: string, expanded: boolean) => void;
  onSelectStepIndex: (i: number | null) => void;
  onSelectProcess?: (id: StableId | null) => void;
  onSelectModule?: (id: StableId) => void;
  onSelectInterface?: (interfaceId: string) => void;
  onArchlangNav?: (href: string) => void;
  subprocessDescriptions?: ReadonlyMap<string, string>;
  onActivateProcess?: (id: StableId) => void;
}) {
  const { t } = useTranslation("sidebar");
  if (processes.length === 0) {
    return <EmptyState message={t("processTab.empty")} />;
  }
  // Picker lives in the workspace navbar now. The details bar only
  // shows the active process tree plus a header with a clear button.
  if (!activeProcess || !walkResult) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        <ProcessList
          processes={processes}
          activeProcessId={activeProcessId}
          onSelectProcess={onSelectProcess}
          onActivateProcess={onActivateProcess}
        />
      </div>
    );
  }
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 min-h-0 overflow-hidden">
        <ProcessTree
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          process={activeProcess as any}
          walkResult={walkResult}
          activeStepIndex={activeStepIndex}
          expandedSubprocesses={walkerState.expandedSubprocesses}
          branchSelection={walkerState.branchSelection}
          onSelectStepIndex={onSelectStepIndex}
          onSelectModule={onSelectModule ? (path) => onSelectModule(path as StableId) : undefined}
          onSelectInterface={onSelectInterface}
          onBranchSelect={onBranchSelect}
          onSubprocessToggle={onSubprocessToggle}
          onClear={() => onSelectProcess?.(null)}
          onArchlangNav={onArchlangNav}
          subprocessDescriptions={subprocessDescriptions}
        />
      </div>
    </div>
  );
}

function ProcessList({
  processes,
  activeProcessId,
  onSelectProcess,
  onActivateProcess,
}: {
  processes: readonly Process[];
  activeProcessId: StableId | null;
  onSelectProcess?: (id: StableId | null) => void;
  onActivateProcess?: (id: StableId) => void;
}) {
  const { t } = useTranslation("sidebar");
  const menu = useContextMenu();
  return (
    <div className="overflow-y-auto max-h-[40vh] border-b border-slate-800">
      <div className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 flex items-center justify-between">
        <span>{t("processTab.listHeading", { count: processes.length })}</span>
        {activeProcessId && (
          <Tooltip content={t("processTab.clearActive")}>
            <Button
              size="xs"
              variant="ghost"
              onClick={() => onSelectProcess?.(null)}
              className="shrink-0 px-1 text-slate-400"
            >
              {t("processTab.clear")}
            </Button>
          </Tooltip>
        )}
      </div>
      <ul className="px-1 pb-2 space-y-0.5">
        {processes.map((p) => {
          const pid = processKey(p);
          const active = pid === activeProcessId;
          const activate = () => {
            if (active) {
              onSelectProcess?.(null);
            } else if (onActivateProcess) {
              onActivateProcess(pid as StableId);
            } else {
              onSelectProcess?.(pid as StableId);
            }
          };
          return (
            <li key={pid}>
              <button
                onClick={activate}
                onContextMenu={(e) =>
                  menu.open(e, [
                    {
                      label: active ? t("processTab.clearActive") : t("menu.open"),
                      onClick: activate,
                    },
                    {
                      label: t("menu.copyName"),
                      separator: true,
                      onClick: () => copyText(p.name),
                    },
                  ])
                }
                className={
                  "w-full text-left px-2 py-1.5 rounded-sm text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 " +
                  (active
                    ? "bg-sky-500/25 text-sky-100 ring-1 ring-sky-500/60"
                    : "text-slate-200 hover:bg-slate-800/(--tint-dim)")
                }
              >
                <div className="flex items-baseline gap-2">
                  <span className="font-medium truncate flex-1">{p.name}</span>
                </div>
                {p.description && (
                  <div className="text-[11px] text-slate-500 truncate mt-0.5">
                    {descriptionToPlainText(p.description)}
                  </div>
                )}
              </button>
            </li>
          );
        })}
      </ul>
      {menu.node}
    </div>
  );
}

